import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "um_admin_auth_v1";

function auth(creds) {
  return "Basic " + btoa(`${creds.user}:${creds.pwd}`);
}

export default function Admin() {
  const [creds, setCreds] = useState(null); // {user, pwd}
  const [userInput, setUserInput] = useState("admin");
  const [pwdInput, setPwdInput] = useState("");
  const [authError, setAuthError] = useState("");

  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  // separate paste textareas per tier
  const [pasteMastermind, setPasteMastermind] = useState("");
  const [pastePremium, setPastePremium] = useState("");
  const [pastePlus, setPastePlus] = useState("");
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberStatus, setMemberStatus] = useState("");
  const [tab, setTab] = useState("knowledge"); // 'knowledge' | 'leads' | 'members'
  const [knowledge, setKnowledge] = useState("");
  const [savedKnowledge, setSavedKnowledge] = useState(""); // last server copy
  const [versions, setVersions] = useState([]);
  const [versionPreview, setVersionPreview] = useState(null); // {id, text, saved_at, char_count}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [transcript, setTranscript] = useState(null); // {lead, messages}

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setCreds(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!creds) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creds]);

  async function loadAll() {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        fetch(`${API}/admin/leads`, {
          headers: { Authorization: auth(creds) },
        }),
        fetch(`${API}/admin/knowledge`, {
          headers: { Authorization: auth(creds) },
        }),
        fetch(`${API}/admin/members`, {
          headers: { Authorization: auth(creds) },
        }),
      ]);
      if (a.status === 401 || b.status === 401 || c.status === 401) {
        setCreds(null);
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthError("Bad credentials");
        return;
      }
      const leadsData = await a.json();
      const kData = await b.json();
      const mData = await c.json();
      setLeads(leadsData);
      setKnowledge(kData.text || "");
      setSavedKnowledge(kData.text || "");
      setMembers(mData || []);
      // Load versions in the background — don't block the main load
      fetch(`${API}/admin/knowledge/versions`, {
        headers: { Authorization: auth(creds) },
      })
        .then((r) => r.json())
        .then((v) => setVersions(Array.isArray(v) ? v : []))
        .catch(() => {});
    } finally {
      setLoading(false);
    }
  }

  async function addMembers(tier, raw) {
    if (!raw.trim()) return;
    setMemberSaving(true);
    setMemberStatus("");
    try {
      const r = await fetch(`${API}/admin/members`, {
        method: "POST",
        headers: {
          Authorization: auth(creds),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ raw_text: raw, tier }),
      });
      if (r.ok) {
        const data = await r.json();
        const parts = [];
        if (data.added) parts.push(`added ${data.added}`);
        if (data.moved) parts.push(`moved ${data.moved} from another tier`);
        const summary = parts.length
          ? parts.join(", ")
          : "no changes (already in this tier)";
        setMemberStatus(
          `${tier.toUpperCase()}: ${summary} ` +
            `(${data.total_submitted} email${
              data.total_submitted === 1 ? "" : "s"
            } detected).`
        );
        if (tier === "mastermind") setPasteMastermind("");
        if (tier === "premium") setPastePremium("");
        if (tier === "plus") setPastePlus("");
        await loadAll();
      } else {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.detail || JSON.stringify(j).slice(0, 200);
        } catch {
          detail = (await r.text()).slice(0, 200);
        }
        setMemberStatus(
          `${tier.toUpperCase()} save failed (${r.status}): ${detail || "no detail"}`
        );
      }
    } catch (err) {
      setMemberStatus(
        `${tier.toUpperCase()} network error: ${err.message || err}`
      );
    } finally {
      setMemberSaving(false);
    }
  }

  async function removeMember(id, email) {
    if (!window.confirm(`Remove ${email} from members?`)) return;
    const r = await fetch(`${API}/admin/members/${id}`, {
      method: "DELETE",
      headers: { Authorization: auth(creds) },
    });
    if (r.ok) {
      await loadAll();
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    const c = { user: userInput.trim(), pwd: pwdInput };
    // Probe once
    const r = await fetch(`${API}/admin/leads`, {
      headers: { Authorization: auth(c) },
    });
    if (r.status === 200) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(c));
      setCreds(c);
      setPwdInput("");
    } else {
      setAuthError("Invalid username or password.");
    }
  }

  async function saveKnowledge() {
    // Safety: warn if the new save is significantly shorter than saved
    const oldLen = savedKnowledge.length;
    const newLen = knowledge.length;
    if (oldLen > 500 && newLen < oldLen * 0.7) {
      const ok = window.confirm(
        `Heads up — you're about to REPLACE ${oldLen.toLocaleString()} ` +
          `characters of saved knowledge with ${newLen.toLocaleString()} ` +
          `characters. That's a big drop.\n\nThe previous version will be ` +
          `backed up automatically, so you can restore it later.\n\nContinue?`
      );
      if (!ok) return;
    }
    setSaving(true);
    setSavedAt(null);
    setSaveError("");
    try {
      const r = await fetch(`${API}/admin/knowledge`, {
        method: "PUT",
        headers: {
          Authorization: auth(creds),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: knowledge }),
      });
      if (r.ok) {
        setSavedAt(new Date().toLocaleTimeString());
        setSavedKnowledge(knowledge);
        // Refresh version list so the new backup shows up
        fetch(`${API}/admin/knowledge/versions`, {
          headers: { Authorization: auth(creds) },
        })
          .then((rr) => rr.json())
          .then((v) => setVersions(Array.isArray(v) ? v : []))
          .catch(() => {});
      } else {
        let detail = "";
        try {
          const j = await r.json();
          detail = j.detail || JSON.stringify(j).slice(0, 200);
        } catch {
          detail = (await r.text()).slice(0, 200);
        }
        setSaveError(
          `Save failed (${r.status}). ${detail || "No detail returned."} ` +
            `Text size: ${knowledge.length.toLocaleString()} chars.`
        );
      }
    } catch (err) {
      setSaveError(`Network error: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  }

  async function previewVersion(v) {
    const r = await fetch(`${API}/admin/knowledge/versions/${v.id}`, {
      headers: { Authorization: auth(creds) },
    });
    if (r.ok) {
      const data = await r.json();
      setVersionPreview(data);
    }
  }

  async function restoreVersion(v) {
    const ok = window.confirm(
      `Restore the version saved ${new Date(v.saved_at).toLocaleString()}?\n\n` +
        `Your current knowledge (${savedKnowledge.length.toLocaleString()} chars) ` +
        `will be backed up before the restore — so this is undoable.`
    );
    if (!ok) return;
    const r = await fetch(
      `${API}/admin/knowledge/versions/${v.id}/restore`,
      {
        method: "POST",
        headers: { Authorization: auth(creds) },
      }
    );
    if (r.ok) {
      setVersionPreview(null);
      await loadAll();
    }
  }

  async function viewTranscript(lead) {
    const r = await fetch(`${API}/admin/leads/${lead.id}/transcript`, {
      headers: { Authorization: auth(creds) },
    });
    const msgs = await r.json();
    setTranscript({ lead, messages: msgs });
  }

  function logout() {
    sessionStorage.removeItem(STORAGE_KEY);
    setCreds(null);
  }

  if (!creds) {
    return (
      <div className="min-h-screen bg-ink-900 text-white flex items-center justify-center p-6">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-ink-800 border border-ink-600 rounded-md p-6 space-y-4"
        >
          <p className="font-display text-xl font-bold uppercase tracking-widest">
            Admin
          </p>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              Username
            </span>
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              className="mt-1 w-full bg-ink-900 border border-ink-600 focus:border-amber rounded-sm px-3 py-2 text-sm text-white outline-none"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              Password
            </span>
            <input
              type="password"
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              className="mt-1 w-full bg-ink-900 border border-ink-600 focus:border-amber rounded-sm px-3 py-2 text-sm text-white outline-none"
              data-testid="admin-password-input"
            />
          </label>
          {authError ? (
            <p className="text-xs text-red-400">{authError}</p>
          ) : null}
          <button
            type="submit"
            data-testid="admin-login-cta"
            className="w-full bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-xs px-4 py-3 rounded-sm transition-colors"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-900 text-white pt-28 pb-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-amber">
              Underground Mix · Admin
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">
              Chatbot Leads &amp; Knowledge
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/admin/leads/export.csv`}
              data-testid="admin-export-csv"
              onClick={(e) => {
                e.preventDefault();
                fetch(`${API}/admin/leads/export.csv`, {
                  headers: { Authorization: auth(creds) },
                })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "um-chatbot-leads.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              className="text-xs uppercase tracking-widest font-mono text-white/70 hover:text-amber border border-ink-600 hover:border-amber px-3 py-2 rounded-sm cursor-pointer"
            >
              Export CSV
            </a>
            <button
              type="button"
              onClick={logout}
              className="text-xs uppercase tracking-widest font-mono text-white/50 hover:text-white"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 border-b border-ink-600">
          {[
            { k: "knowledge", label: "Knowledge" },
            { k: "leads", label: `Leads (${leads.length})` },
            { k: "members", label: `Members (${members.length})` },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-4 py-3 text-xs uppercase tracking-widest font-mono transition-colors border-b-2 ${
                tab === t.k
                  ? "text-amber border-amber"
                  : "text-white/55 hover:text-white border-transparent"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "knowledge" ? (
          /* Knowledge editor */
          <section
            data-testid="admin-knowledge-section"
            className="bg-ink-800 border border-ink-600 rounded-md p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-bold uppercase tracking-widest">
                Knowledge base
              </h2>
              {savedAt ? (
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber">
                  Saved · {savedAt}
                </span>
              ) : null}
            </div>
            <p className="text-xs text-white/55 mb-3">
              Paste sync facts, FAQs, and your real-world examples. The
              chatbot uses this as ground truth. Plain text or Markdown both
              work.
            </p>
            <textarea
              data-testid="admin-knowledge-textarea"
              value={knowledge}
              onChange={(e) => setKnowledge(e.target.value)}
              spellCheck={false}
              className="w-full h-[520px] bg-ink-900 border border-ink-600 focus:border-amber rounded-sm p-3 text-sm text-white font-mono leading-relaxed outline-none"
            />
            <div className="flex items-center justify-between gap-3 mt-3">
              {saveError ? (
                <p
                  data-testid="admin-save-error"
                  className="text-xs text-red-400 leading-snug"
                >
                  {saveError}
                </p>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={saveKnowledge}
                disabled={saving}
                data-testid="admin-save-knowledge"
                className="bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors disabled:opacity-60 shrink-0"
              >
                {saving ? "Saving…" : "Save knowledge"}
              </button>
            </div>

            {/* Version history */}
            <div className="mt-8 pt-6 border-t border-ink-600">
              <h3 className="font-display text-sm font-bold uppercase tracking-widest mb-2">
                Previous versions
              </h3>
              <p className="text-xs text-white/55 mb-3">
                Every save automatically backs up the prior version. Last
                {" "}{versions.length} of 20 retained. Click a row to preview
                or restore.
              </p>
              {versions.length === 0 ? (
                <p className="text-xs text-white/40">
                  No previous versions yet. The next save will create one.
                </p>
              ) : (
                <div className="divide-y divide-ink-600 border border-ink-600 rounded-sm max-h-[200px] overflow-y-auto">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="text-white truncate">
                          {new Date(v.saved_at).toLocaleString()}
                        </p>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                          {v.char_count.toLocaleString()} chars
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => previewVersion(v)}
                          data-testid={`version-preview-${v.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-white border border-ink-600 hover:border-amber px-2 py-1 rounded-sm"
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => restoreVersion(v)}
                          data-testid={`version-restore-${v.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-amber hover:text-black hover:bg-amber border border-amber px-2 py-1 rounded-sm"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        ) : null}

        {tab === "leads" ? (
          /* Leads list */
          <section
            data-testid="admin-leads-section"
            className="bg-ink-800 border border-ink-600 rounded-md p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-bold uppercase tracking-widest">
                Leads
              </h2>
              <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                {loading ? "Loading…" : `${leads.length} total`}
              </span>
            </div>
            <div className="max-h-[640px] overflow-y-auto divide-y divide-ink-600">
              {leads.length === 0 && !loading ? (
                <p className="text-sm text-white/40 py-8 text-center">
                  No leads yet. Once visitors start chatting, they'll show up
                  here.
                </p>
              ) : null}
              {leads.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => viewTranscript(l)}
                  data-testid={`lead-row-${l.id}`}
                  className="w-full text-left py-3 hover:bg-ink-900/40 px-2 rounded-sm transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate flex items-center gap-2">
                        {l.name}
                        {l.is_member ? (
                          <span className="text-[9px] bg-amber text-black font-bold tracking-widest px-1.5 py-0.5 rounded-sm uppercase">
                            {l.tier || "member"}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-white/55 truncate">
                        {l.email}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                        {new Date(l.created_at).toLocaleString()}
                      </p>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-amber mt-0.5">
                        {l.message_count} msg
                        {l.kajabi_pushed ? " · synced" : ""}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {tab === "members" ? (
          <section
            data-testid="admin-members-section"
            className="space-y-6"
          >
            <div className="bg-ink-800 border border-ink-600 rounded-md p-5">
              <p className="text-xs text-white/55 leading-relaxed mb-1">
                Tier rules:
                <span className="text-amber"> Mastermind</span> = unlimited ·
                <span className="text-amber"> Premium</span> = 30 / quarter ·
                <span className="text-amber"> Plus</span> = 20 / quarter ·
                Everyone else = 10 lifetime (no reset).
                If an email appears in multiple tiers, the highest tier wins.
              </p>
              {memberStatus ? (
                <p className="text-xs text-amber mt-2">{memberStatus}</p>
              ) : null}
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              {[
                {
                  tier: "mastermind",
                  label: "Mastermind",
                  cap: "Unlimited",
                  paste: pasteMastermind,
                  setPaste: setPasteMastermind,
                },
                {
                  tier: "premium",
                  label: "ACCESS PREMIUM",
                  cap: "30 / quarter",
                  paste: pastePremium,
                  setPaste: setPastePremium,
                },
                {
                  tier: "plus",
                  label: "ACCESS PLUS",
                  cap: "20 / quarter",
                  paste: pastePlus,
                  setPaste: setPastePlus,
                },
              ].map((t) => {
                const tierMembers = members.filter(
                  (m) => (m.tier || "mastermind") === t.tier
                );
                return (
                  <div
                    key={t.tier}
                    className="bg-ink-800 border border-ink-600 rounded-md p-5 flex flex-col"
                    data-testid={`members-tier-${t.tier}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="font-display text-sm font-bold uppercase tracking-widest text-amber">
                        {t.label}
                      </h2>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                        {t.cap}
                      </span>
                    </div>
                    <textarea
                      value={t.paste}
                      onChange={(e) => t.setPaste(e.target.value)}
                      spellCheck={false}
                      placeholder={"member@example.com\nanother@example.com"}
                      data-testid={`paste-${t.tier}`}
                      className="w-full h-[120px] bg-ink-900 border border-ink-600 focus:border-amber rounded-sm p-2 text-xs text-white font-mono leading-relaxed outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => addMembers(t.tier, t.paste)}
                      disabled={memberSaving || !t.paste.trim()}
                      data-testid={`add-${t.tier}`}
                      className="mt-2 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors disabled:opacity-60"
                    >
                      {memberSaving ? "Adding…" : `Add to ${t.label}`}
                    </button>

                    <div className="mt-4 pt-3 border-t border-ink-600">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                        Current · {tierMembers.length}
                      </p>
                      <div className="max-h-[260px] overflow-y-auto divide-y divide-ink-600 border border-ink-600 rounded-sm">
                        {tierMembers.length === 0 ? (
                          <p className="text-xs text-white/40 py-4 text-center">
                            None yet.
                          </p>
                        ) : null}
                        {tierMembers.map((m) => (
                          <div
                            key={m.id}
                            className="flex items-center justify-between gap-2 px-2 py-1.5"
                          >
                            <p className="text-xs text-white truncate flex-1">
                              {m.email}
                            </p>
                            <button
                              type="button"
                              onClick={() => removeMember(m.id, m.email)}
                              data-testid={`remove-${m.id}`}
                              className="text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-red-400"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>

      {/* Transcript drawer */}
      {transcript ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setTranscript(null)}
        >
          <div
            className="bg-ink-900 border border-ink-600 rounded-md max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-600">
              <div>
                <p className="font-display text-base font-bold">
                  {transcript.lead.name}
                </p>
                <p className="text-xs text-white/55">
                  {transcript.lead.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTranscript(null)}
                className="text-xs uppercase tracking-widest font-mono text-white/50 hover:text-white"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {transcript.messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] text-sm leading-relaxed px-3 py-2 rounded-md whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-amber text-black"
                        : "bg-ink-800 text-white/90 border border-ink-600"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {transcript.messages.length === 0 ? (
                <p className="text-sm text-white/40 text-center py-8">
                  No messages yet.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Version preview drawer */}
      {versionPreview ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setVersionPreview(null)}
        >
          <div
            className="bg-ink-900 border border-ink-600 rounded-md max-w-3xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-600">
              <div>
                <p className="font-display text-base font-bold">
                  Version preview
                </p>
                <p className="text-xs text-white/55">
                  Saved {new Date(versionPreview.saved_at).toLocaleString()} ·
                  {" "}
                  {versionPreview.char_count.toLocaleString()} chars
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    restoreVersion({
                      id: versionPreview.id,
                      saved_at: versionPreview.saved_at,
                    })
                  }
                  className="text-xs uppercase tracking-widest font-mono text-amber border border-amber hover:bg-amber hover:text-black px-3 py-2 rounded-sm"
                >
                  Restore this
                </button>
                <button
                  type="button"
                  onClick={() => setVersionPreview(null)}
                  className="text-xs uppercase tracking-widest font-mono text-white/50 hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
            <pre className="flex-1 overflow-y-auto p-5 text-xs text-white/85 font-mono whitespace-pre-wrap leading-relaxed">
              {versionPreview.text}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}
